import { Injectable, RendererFactory2, inject, DOCUMENT } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';

import { isAutomatedTestSession } from '@ninety/platform/services/services/automated-test-session';
import { environment } from '@ninety/ui/web/environments';

import { LoggingActions } from '../../../app-logging/app-logging.actions';

declare let _hsq: any;

@Injectable({
  providedIn: 'root',
})
export class HubspotService {
  private rendererFactory = inject(RendererFactory2);
  private document = inject<Document>(DOCUMENT);
  private store = inject(Store);

  private isLoaded$ = new BehaviorSubject<boolean>(false);
  private renderer = this.rendererFactory.createRenderer(this.document.head, null);

  // Automated sign-ups must not create HubSpot contacts or page views. Resolved once here rather
  // than per call because loadScript runs from the constructor, before any route state exists.
  private readonly disabled = isAutomatedTestSession();

  constructor() {
    this.loadScript();
  }

  loaded(): Observable<boolean> {
    return this.isLoaded$.asObservable();
  }

  private loadScript(): void {
    if (this.disabled) return;

    if (window.hasOwnProperty('_hsq')) {
      this.isLoaded$.next(true);
      return;
    }

    const script: HTMLScriptElement = this.renderer.createElement('script');

    script.onload = () => this.isLoaded$.next(true);

    script.src = `https://js.hs-scripts.com/${environment.HUBSPOT_API_KEY}.js`;
    this.renderer.appendChild(this.document.head, script);
  }

  identifyContact(email: string): void {
    // _hsq only exists once the script has loaded, so these guards also keep the catch blocks below
    // from error-logging on every call for a session that deliberately skipped it.
    if (this.disabled) return;

    try {
      _hsq.push(['identify', { email }]);
      this.trackPageView();
    } catch (e) {
      this.store.dispatch(
        LoggingActions.error({
          log: {
            message: '[Hubspot] Error identifying contact',
            error: e,
            data: {
              hubspot_hsq: _hsq ?? 'not loaded',
            },
          },
        })
      );
    }
  }

  setPath(path: string): void {
    if (this.disabled) return;

    try {
      _hsq.push(['setPath', { path }]);
      this.trackPageView();
    } catch (e) {
      this.store.dispatch(
        LoggingActions.error({
          log: {
            message: '[Hubspot] Error setting path',
            error: e,
            data: {
              hubspot_hsq: _hsq ?? 'not loaded',
            },
          },
        })
      );
    }
  }

  trackPageView(): void {
    if (this.disabled) return;

    try {
      _hsq.push(['trackPageView']);
    } catch (e) {
      this.store.dispatch(
        LoggingActions.error({
          log: {
            message: '[Hubspot] Error tracking page view',
            error: e,
            data: {
              hubspot_hsq: _hsq ?? 'not loaded',
            },
          },
        })
      );
    }
  }
}
